import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cascader from "rc-cascader";
import { TextField } from "@material-ui/core";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { useModulesManager, useTranslations } from "@openimis/fe-core";
import { fetchLocationsStr } from "../actions";
import { locationLabel } from "../utils";

const styles = () => ({
  root: {
    width: "100%",
  },
});

const extractPathFromValue = (location) => {
  const names = [];
  const uuids = [];

  let current = location;
  while (current) {
    names.unshift(current.name);
    uuids.unshift(current.uuid);
    current = current.parent;
  }

  return {
    names,
    uuids,
  };
};

const LocationCascader = ({
  label = "Location",
  onChange,
  readOnly,
  classes,
  value,
}) => {
  const modulesManager = useModulesManager();
  const { formatMessage } = useTranslations("location", modulesManager);
  const dispatch = useDispatch();
  const locState = useSelector((state) => state.loc);

  const maxLevel = parseInt(
    modulesManager.getConf("location", "Location.MaxLevels", 4)
  );

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [defaultValue, setDefaultValue] = useState([]);

  const pendingExpansion = useRef(null);

  useEffect(() => {
    dispatch(fetchLocationsStr(modulesManager, 0));
  }, []);

  useEffect(() => {
    const l0s = (locState.l0s || []).map((loc) => ({
      label: locationLabel(loc),
      value: loc.uuid,
      isLeaf: false,
      level: 0,
      raw: loc,
    }));
    setOptions(l0s);
  }, [locState.l0s]);

  const loadData = (selectedOptions) => {
    const targetOption = selectedOptions[selectedOptions.length - 1];
    const currentLevel = targetOption.level;

    // --- THE JUMP LOGIC ---
    // If current is Region (0), next is District (1)
    // If current is District (1), next is Village (3) - Skipping Ward (2)
    let nextLevelToFetch = currentLevel + 1;

    if (currentLevel === 1) {
      nextLevelToFetch = 3;
    }

    // Stop if we are already at Village level (3)
    if (currentLevel === 3) {
      targetOption.isLeaf = true;
      return;
    }

    targetOption.loading = true;
    pendingExpansion.current = { targetOption, level: nextLevelToFetch };

    // Request Level 1 or Level 3
    dispatch(fetchLocationsStr(modulesManager, nextLevelToFetch, null, null, targetOption.raw));
  };

  useEffect(() => {
    if (!pendingExpansion.current) return;

    const { targetOption, level } = pendingExpansion.current;

    // Wait for the specific jumped level (1 or 3) to arrive in Redux
    if (!locState[`fetchedL${level}s`]) return;

    const children = (locState[`l${level}s`] || [])
      .map((loc) => ({
        label: locationLabel(loc),
        value: loc.uuid,
        // level 3 is the Village level, so it's a leaf
        isLeaf: level === 3,
        level: level,
        raw: loc,
      }));

    targetOption.loading = false;
    targetOption.children = children;

    setOptions([...options]);
    pendingExpansion.current = null;
  }, [locState]);

  useEffect(() => {
    if (value?.uuid) {
      const { uuids } = extractPathFromValue(value);
      setDefaultValue(uuids);
      setInputValue(locationLabel(value));
    } else {
      setDefaultValue([]);
      setInputValue("");
    }
  }, [value]);

  const handleCascaderChange = (uuids, selectedOptions) => {
    const selected = selectedOptions[selectedOptions.length - 1];
    setInputValue(selected?.label || "");

    onChange?.(selected.raw, locationLabel(selected.raw));
  };

  return (
    <div className={classes.root}>
      <Cascader
        options={options}
        defaultValue={defaultValue}
        loadData={loadData}
        onChange={handleCascaderChange}
        changeOnSelect={true}
        disabled={readOnly}
        expandIcon={<KeyboardArrowRightIcon fontSize="small" />}
        loadingIcon={<AutorenewIcon fontSize="small" className="spin" />}
      >
        <TextField
          label={label || formatMessage("LocationPicker.label")}
          value={inputValue}
          fullWidth
          disabled={readOnly}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <ArrowDropDownIcon
                style={{ color: "rgba(0, 0, 0, 0.54)" }}
              />
            ),
          }}
        />
      </Cascader>
    </div>
  );
};

export default withStyles(styles)(withTheme(LocationCascader));
